import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GscConnectionService, type GscConnectionView } from '../../services/gsc-connection.service';
import { NotificationService } from '../../services/notification.service';

/**
 * The Search Console line in Setup — GSC_INTEGRATION.md stage 1 (§4.6, Q4).
 *
 * **Renders nothing unless the workspace connected Search Console** and this Setup page is the
 * connected site (§1a: no prompts or labels without GSC). Connecting is an operator action with an
 * explicit property (`npm run gsc:connect`); this line shows the result, the country scope, and
 * the editable brand list.
 */
@Component({
  selector: 'app-gsc-connection',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gsc-connection.html',
  styleUrl: './gsc-connection.css'
})
export class GscConnectionComponent implements OnChanges, OnDestroy {
  /** The Setup page's domain. The line shows only when it is the connected site. */
  @Input() domain: string | null = null;

  connection: GscConnectionView | null = null;
  draftBrandTerms = '';
  saving = false;
  syncing = false;
  private poll: ReturnType<typeof setInterval> | null = null;

  /** While a sync runs: the back-fill takes minutes, and the line should say when it is done. */
  private readonly SYNC_POLL_MS = 10_000;

  constructor(
    private gsc: GscConnectionService,
    private notifications: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['domain']) this.load();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /** True when there is a connection for the site this Setup page is about. */
  get visible(): boolean {
    const domain = (this.domain || '').trim().toLowerCase().replace(/^www\./, '');
    return !!this.connection && !!domain && this.connection.domain === domain;
  }

  load(): void {
    this.gsc.get().subscribe({
      next: (res) => this.apply(res?.data ?? null),
      // A workspace without GSC must look exactly as before, so a failed read shows nothing too.
      error: () => this.apply(null)
    });
  }

  private apply(view: GscConnectionView | null): void {
    this.connection = view;
    this.draftBrandTerms = view ? view.brandTerms.join(', ') : '';
    if (this.syncing && view && view.status !== 'pending' && !this.poll) this.syncing = false;
  }

  statusClass(): string {
    switch (this.connection?.status) {
      case 'ok':
        return 'badge--success';
      case 'pending':
        return 'badge--info';
      case 'expired':
      case 'no_access':
        return 'badge--warning';
      default:
        return 'badge--danger';
    }
  }

  statusLabel(): string {
    switch (this.connection?.status) {
      case 'ok':
        return 'Connected';
      case 'pending':
        return 'First sync';
      case 'expired':
        return 'Sign-in expired';
      case 'no_access':
        return 'Access removed';
      default:
        return 'Sync failed';
    }
  }

  brandTermsChanged(): boolean {
    return this.parseBrandTerms().join('\n') !== (this.connection?.brandTerms ?? []).join('\n');
  }

  private parseBrandTerms(): string[] {
    const out: string[] = [];
    for (const t of this.draftBrandTerms.split(',')) {
      const v = t.trim().toLowerCase().replace(/\s+/g, ' ');
      if (v && !out.includes(v)) out.push(v);
    }
    return out;
  }

  saveBrandTerms(): void {
    if (this.saving) return;
    this.saving = true;
    this.gsc.updateBrandTerms(this.parseBrandTerms()).subscribe({
      next: (res) => {
        this.apply(res.data);
        this.saving = false;
        this.notifications.success('Brand terms saved. Page summaries recomputed.');
      },
      error: (err) => {
        this.saving = false;
        this.notifications.error(err?.error?.error || 'The brand terms could not be saved.');
      }
    });
  }

  syncNow(): void {
    if (this.syncing) return;
    this.syncing = true;
    this.gsc.sync().subscribe({
      next: () => this.startPolling(),
      error: (err) => {
        this.syncing = false;
        this.notifications.error(err?.status === 409 ? 'A sync is already running.' : 'The sync could not be started.');
      }
    });
  }

  private startPolling(): void {
    this.stopPolling();
    const startedAt = Date.now();
    const before = this.connection?.lastSyncedAt ?? null;
    this.poll = setInterval(() => {
      this.gsc.get().subscribe((res) => {
        const view = res?.data ?? null;
        const finished = !!view && (view.lastSyncedAt !== before || (view.status !== 'ok' && view.status !== 'pending'));
        this.connection = view;
        if (finished || Date.now() - startedAt > 30 * 60_000) {
          this.stopPolling();
          this.syncing = false;
          this.apply(view);
        }
      });
    }, this.SYNC_POLL_MS);
  }

  private stopPolling(): void {
    if (this.poll) {
      clearInterval(this.poll);
      this.poll = null;
    }
  }
}
